import { CarbonTarget, EntityLabel } from '@esg/esg-global-types';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { apollo_client } from './db_util';
import { DocumentReference } from 'firebase/firestore';
import { ApolloQueryResult, FetchResult } from '@apollo/client';
import { GET_CARBON_TARGETS_QUERY } from '../graphql/queries/carbon_target';
import {
  CREATE_CARBON_TARGET_MUTATOR,
  DELETE_CARBON_TARGET_MUTATOR,
  UPDATE_CARBON_TARGET_MUTATOR
} from '../graphql/mutations/carbon_target';
import { Moment } from 'moment';

export interface CarbonTargetData extends Omit<CarbonTarget, 'id'> {
  created: Date | Moment;
  modified: Date | Moment | null;
  deleted: Date | Moment | null;
}

// Singular and plural label for model entity.
export const carbon_target_label: EntityLabel = {
  one: 'Carbon Target',
  many: 'Carbon Targets'
};

/**
 * Query all carbon target documents for company.
 * @param {string} group_id Group to query documents for.
 * @param {string} company_id Company to query documents for.
 * @param {string | undefined} id query a specific Carbon Target by id.
 * @returns {Promise<Array<CarbonTargetAdmin>>}
 */
export const getCarbonTargets = async (
  group_id: string,
  company_id: string,
  id?: string
): Promise<Array<CarbonTarget>> => {
  try {
    const carbon_targets: Array<CarbonTarget> = await apollo_client
      .query({
        query: GET_CARBON_TARGETS_QUERY,
        variables: { group_id: group_id, company_id: company_id, id: id },
        fetchPolicy: 'no-cache'
      })
      .then(
        (response: ApolloQueryResult<{ carbon_targets: Array<CarbonTarget> }>) =>
          response.data.carbon_targets
      );
    return carbon_targets;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getCarbonTargets failed on an unknown error.',
      {
        group_id: group_id,
        company_id: company_id,
        id: id
      },
      tracking_id
    );
  }
};

/**
 * Create Carbon Target with relative data.
 * @param {string} group_id ID of Group which Carbon Target is being created for.
 * @param {string} company_id ID of Company which Carbon Target is being created for.
 * @param {CarbonTargetData} carbon_target Carbon Target data to create document with.
 * @param {string} user_email User creating Carbon Target for audit logs.
 * @returns {Promise<DocumentReference>}
 */
export const createCarbonTarget = async (
  group_id: string,
  company_id: string,
  carbon_target_data: CarbonTargetData,
  user_email: string
): Promise<DocumentReference> => {
  try {
    const carbon_target_doc: DocumentReference = await apollo_client
      .mutate({
        mutation: CREATE_CARBON_TARGET_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          carbon_target_data: carbon_target_data,
          user_email: user_email
        }
      })
      .then(
        (response: FetchResult<{ createCarbonTarget: DocumentReference }>) =>
          response.data!.createCarbonTarget
      );
    return carbon_target_doc;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/carbon_target.ts failed on an unknown error while calling createCarbonTarget.',
      {
        group_id: group_id,
        company_id: company_id,
        carbon_target_data: carbon_target_data,
        user_email: user_email
      },
      tracking_id
    );
  }
};

/**
 * Update Carbon Target with relative data.
 * @param {string} group_id ID of Group to update Carbon Target for.
 * @param {string} company_id ID of Company to update Carbon Target for.
 * @param {string} carbon_target_id ID of Carbon Target to update.
 * @param {CarbonTargetData} updated_carbon_target New Carbon Target data to push into document.
 * @param {CarbonTargetData} original_carbon_target Original Carbon Target data to overwrite.
 * @param {string} user_email User updating Carbon Target for audit logs.
 * @returns {Promise<void>}
 */
export const updateCarbonTarget = async (
  group_id: string,
  company_id: string,
  carbon_target_id: string,
  updated_carbon_target: CarbonTargetData,
  original_carbon_target: CarbonTargetData,
  user_email: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_CARBON_TARGET_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          carbon_target_id: carbon_target_id,
          updated_carbon_target: updated_carbon_target,
          original_carbon_target: original_carbon_target,
          user_email: user_email
        }
      })
      .then(
        (response: FetchResult<{ updateCarbonTarget: DocumentReference }>) =>
          response.data!.updateCarbonTarget
      );
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/carbon_target.ts failed on an unknown error while calling updateCarbonTarget.',
      {
        group_id: group_id,
        company_id: company_id,
        carbon_target_id: carbon_target_id,
        updated_carbon_target: updated_carbon_target,
        original_carbon_target: original_carbon_target,
        user_email
      },
      tracking_id
    );
  }
};

/**
 * Delete configured Carbon Target.
 * @param {string} group_id ID of Group to delete Carbon Target for.
 * @param {string} company_id ID of Company to delete Carbon Target for.
 * @param {string} carbon_target_id ID of Carbon Target to delete.
 * @param {string} user_email User deleting Carbon Target for audit logs.
 * @returns {Promise<void>}
 */
export const deleteCarbonTarget = async (
  group_id: string,
  company_id: string,
  carbon_target_id: string,
  user_email: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_CARBON_TARGET_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          carbon_target_id: carbon_target_id,
          user_email: user_email
        }
      })
      .then(
        (response: FetchResult<{ deleteCarbonTarget: DocumentReference }>) =>
          response.data!.deleteCarbonTarget
      );
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/carbon_target.ts failed on an unknown error while calling deleteCarbonTarget.',
      {
        group_id: group_id,
        company_id: company_id,
        carbon_target_id: carbon_target_id,
        user_email: user_email
      },
      tracking_id
    );
  }
};
